import React, { useEffect, useState } from 'react';
import api from '../api';
import { Modal, Button, Form, Table, InputGroup, Pagination } from 'react-bootstrap';
import BackButton from '../components/BackButton';

export default function ProductPage() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ Name: '', HSN: '', Rate: '', GSTPercent: '' });
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const paginatedProducts = products.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(products.length / itemsPerPage);

  // Fetch products (with optional search)
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/products', { params: { search } });
      setProducts(res.data);
      setCurrentPage(1); // Reset to first page on new search
    } catch {
      alert('Failed to fetch products');
    }
    setLoading(false);
  };

  useEffect(() => { fetchProducts(); }, [search]);

  // Open modal for add/edit
  const handleShowModal = (product = null) => {
    setEditing(product);
    setForm(product ? { ...product } : { Name: '', HSN: '', Rate: '', GSTPercent: '' });
    setShowModal(true);
  };

  // Handle form changes
  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Add or update product, with validation
  const handleSave = async () => {
    // Name required
    if (!form.Name.trim()) {
      alert('Product Name is required');
      return;
    }
    // Price required and must be positive
    if (!form.Rate || isNaN(form.Rate) || Number(form.Rate) <= 0) {
      alert('Please enter a valid positive price');
      return;
    }
    // GST: optional, but if entered, must be 0-28
    if (form.GSTPercent !== "" && form.GSTPercent !== null && form.GSTPercent !== undefined) {
      if (isNaN(form.GSTPercent) || Number(form.GSTPercent) < 0 || Number(form.GSTPercent) > 28) {
        alert('GST must be between 0 and 28');
        return;
      }
    }

    setLoading(true);
    try {
      if (editing) {
        await api.put(`/products/${editing.ProductID}`, {
          name: form.Name,
          hsn: form.HSN,
          rate: form.Rate,
          gstPercent: form.GSTPercent || 0,
        });
      } else {
        await api.post('/products', {
          name: form.Name,
          hsn: form.HSN,
          rate: form.Rate,
          gstPercent: form.GSTPercent || 0,
        });
      }
      setShowModal(false);
      fetchProducts();
    } catch {
      alert('Failed to save product');
    }
    setLoading(false);
  };

  // Delete product
  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    setLoading(true);
    try {
      await api.delete(`/products/${productId}`);
      fetchProducts();
    } catch {
      alert('Failed to delete product');
    }
    setLoading(false);
  };

  // Pagination controls
  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="container py-4">
      {/* Header Row */}
      <div className="row align-items-center mb-3 gx-2">
        <div className="col-auto">
          <BackButton />
        </div>
        <div className="col">
          <h3 className="fw-bold mb-0" style={{ fontSize: 26, letterSpacing: 0.2 }}>Product Management</h3>
        </div>
        <div className="col-auto">
          <Button
            variant="primary"
            onClick={() => handleShowModal()}
            className="rounded-pill px-4 fw-semibold"
            style={{ whiteSpace: 'nowrap' }}
          >
            + Add Product
          </Button>
        </div>
      </div>

      <InputGroup className="mb-3" style={{ maxWidth: 320 }}>
        <Form.Control
          placeholder="Search by name or HSN"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Button variant="outline-secondary" onClick={fetchProducts}>Search</Button>
      </InputGroup>

      <Table striped bordered hover size="sm" className="rounded shadow-sm" style={{ background: '#fff' }}>
        <thead className="table-primary">
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>HSN</th>
            <th>Rate</th>
            <th>GST (%)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedProducts.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center text-muted">No products found.</td>
            </tr>
          ) : (
            paginatedProducts.map((prod, idx) => (
              <tr key={prod.ProductID}>
                <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                <td>{prod.Name}</td>
                <td>{prod.HSN}</td>
                <td>₹ {Number(prod.Rate).toFixed(2)}</td>
                <td>{Number(prod.GSTPercent).toFixed(2)}</td>
                <td>
                  <Button variant="info" size="sm" className="me-2" onClick={() => handleShowModal(prod)}>
                    Edit
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(prod.ProductID)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination className="justify-content-center">
          <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
          {Array.from({ length: totalPages }, (_, i) => (
            <Pagination.Item
              key={i + 1}
              active={i + 1 === currentPage}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      )}

      {/* Modal for add/edit */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Product' : 'Add Product'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                name="Name"
                value={form.Name}
                onChange={handleChange}
                autoFocus
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>HSN</Form.Label>
              <Form.Control name="HSN" value={form.HSN} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Rate <span className="text-danger">*</span></Form.Label>
              <Form.Control
                name="Rate"
                type="number"
                min={0}
                step="0.01"
                value={form.Rate}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>GST (%)</Form.Label>
              <Form.Control
                name="GSTPercent"
                type="number"
                min={0}
                max={28}
                placeholder="GST (0-28%)"
                value={form.GSTPercent}
                onChange={handleChange}
              />
              <Form.Text muted>Optional. Leave blank if not applicable.</Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : (editing ? 'Update' : 'Add')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
